import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import "../../styles/PatientBillingForm.css";
import { useAuth } from "../../hooks/useAuth";
import SalesView from "../SalesView"; // Update import from SalesForm to SalesView
import PayThroughAccountModal from "./PayThroughAccountModal";
import SplitPaymentModal from "./SplitPaymentModal";
import { AdmissionInvoicePrompt } from "../Procedures/BillSurgeryModal";
import { getProcedureAgreedTotal, formatAgreedAmount } from "../Procedures/procedureBilling";
import { labTestDisplayName } from "../../utils/labTestDisplayName";

const OLDER_EDIT_KEY = "/sales-transactions/edit-older";
const AGE_RESTRICTED_FALLBACK =
  "This sales record is older than 2 days. Permission to edit older records is required.";

const salesApiBase = () =>
  `${process.env.REACT_APP_SERVER_BASE_URL}${process.env.REACT_APP_API_SALES_TRANSACTIONS}`;

const isOlderThanTwoDays = (salesDate) => {
  if (!salesDate) return true;
  const sale = new Date(`${String(salesDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(sale.getTime())) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today.getTime() - sale.getTime()) / (24 * 60 * 60 * 1000));
  return diffDays > 2;
};

const formatBillDate = (dateValue) => {
  if (!dateValue) return null;
  const dateOnly = String(dateValue).slice(0, 10);
  const parsed = new Date(`${dateOnly}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(dateValue);
  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const PatientBillingForm = ({ patient, bill, onClose }) => {
  const [billedItems, setBilledItems] = useState(null);
  // Update state to handle multiple selections
  const [formData, setFormData] = useState(bill || {});
  const [billData, setBillData] = useState(null);
  const [selectedLabRequests, setSelectedLabRequests] = useState(new Set());
  const [selectedRadiologyRequests, setSelectedRadiologyRequests] = useState(new Set());
  const [selectedVisits, setSelectedVisits] = useState(new Set());
  const [selectedProcedures, setSelectedProcedures] = useState(new Set());
  const [procedureEstimate, setProcedureEstimate] = useState(0);
  const [showSurgeryMergePrompt, setShowSurgeryMergePrompt] = useState(false);
  const [pendingAdmission, setPendingAdmission] = useState(null);
  const skipSurgeryMergePromptRef = useRef(false);
  const surgeryMergeAdmissionRef = useRef(null);
  const [salesNumber, setSalesNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [branch, setBranch] = useState("");
  const [customerId, setCustomerId] = useState(null);
  const [careoffName, setCareoffName] = useState("");
  const [salesViewRefresh, setSalesViewRefresh] = useState(0); // New state variable for SalesView refresh
  // Add edit state for visit quantities
  const [editingVisits, setEditingVisits] = useState(new Set());
  const [modifiedQuantities, setModifiedQuantities] = useState({});
  const [doctorLineItems, setDoctorLineItems] = useState(new Map()); // Map of visit_id -> doctor info
  // New state for pay through account modal
  const [showPayThroughModal, setShowPayThroughModal] = useState(false);
  const [showSplitPaymentModal, setShowSplitPaymentModal] = useState(false);
  const [payThroughAccount, setPayThroughAccount] = useState(null);
  const [splitPayments, setSplitPayments] = useState(null);
  const [registerClosed, setRegisterClosed] = useState(false);
  const [ageEditRestricted, setAgeEditRestricted] = useState(null);
  const [ageEditReason, setAgeEditReason] = useState("");

  const hasPendingVisitEdits = editingVisits.size > 0;

  const { hasPermission } = useAuth();
  const canEditOlder = hasPermission(OLDER_EDIT_KEY, "Edit");
  const isAgeRestricted =
    ageEditRestricted != null
      ? ageEditRestricted
      : Boolean(bill && isOlderThanTwoDays(bill.date));
  const cannotUpdateOldBill = Boolean(bill) && isAgeRestricted && !canEditOlder;
  // New state variables for detailed line items when an ID exists in formData.
  const [fetchedLabRequest, setFetchedLabRequest] = useState(null);
  const [fetchedRadiologyRequest, setFetchedRadiologyRequest] = useState(null);
  const [fetchedVisit, setFetchedVisit] = useState(null);
  const [visitEstimate, setVisitEstimate] = useState(0);
  const [labEstimate, setLabEstimate] = useState(0);
  const [radiologyEstimate, setRadiologyEstimate] = useState(0);
  const [kitNames, setKitNames] = useState({});

  const estimatedBillTotal =
    Number(visitEstimate) + Number(labEstimate) + Number(radiologyEstimate) + Number(procedureEstimate);
  const allowSplitPayment =
    patient?.patient_type === "Private" &&
    !customerId &&
    !bill?.zoho_sales_id;

  const { user } = useAuth();

  const customerOptions = [
    { id: "4691741000000115035", name: "Admission Private Patient" },
    { id: "4691741000000115145", name: "Careoff" },
  ];

  useEffect(() => {
    if (!patient) {
      setLoading(false);
      return;
    }
    const fetchBillData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/billing/visit-bill/${patient.patient_id}`
        );
        setBillData(response.data);
      } catch (error) {
        console.error("Error fetching billing data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBillData();
  }, [patient]);

  useEffect(() => {
    if (!bill) {
      setAgeEditRestricted(null);
      setAgeEditReason("");
      return;
    }

    const salesNumber = bill.sales_number;
    if (!salesNumber) {
      const fallbackRestricted = isOlderThanTwoDays(bill.date);
      setAgeEditRestricted(fallbackRestricted);
      setAgeEditReason(fallbackRestricted ? AGE_RESTRICTED_FALLBACK : "");
      return;
    }

    let cancelled = false;
    const loadAgePolicy = async () => {
      try {
        const response = await axios.get(
          `${salesApiBase()}/bySalesNumber/${encodeURIComponent(salesNumber)}`
        );
        if (cancelled) return;
        if (typeof response.data?.ageEditRestricted === "boolean") {
          setAgeEditRestricted(response.data.ageEditRestricted);
          setAgeEditReason(response.data.ageEditReason || "");
          return;
        }
        const fallbackRestricted = isOlderThanTwoDays(
          response.data?.header?.sales_date || bill.date
        );
        setAgeEditRestricted(fallbackRestricted);
        setAgeEditReason(fallbackRestricted ? AGE_RESTRICTED_FALLBACK : "");
      } catch (error) {
        if (cancelled) return;
        const fallbackRestricted = isOlderThanTwoDays(bill.date);
        setAgeEditRestricted(fallbackRestricted);
        setAgeEditReason(fallbackRestricted ? AGE_RESTRICTED_FALLBACK : "");
      }
    };
    loadAgePolicy();
    return () => {
      cancelled = true;
    };
  }, [bill]);

  useEffect(() => {
    if (!branch) {
      setRegisterClosed(false);
      return;
    }

    const fetchRegisterStatus = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/cash-register/status`,
          { params: { branch } }
        );
        setRegisterClosed(!response.data.isOpen);
      } catch (error) {
        console.error('Error fetching cash register status:', error);
        setRegisterClosed(false);
      }
    };

    fetchRegisterStatus();
  }, [branch]);


  const handleCheckboxChangeLab = async (labId) => {
    const newSelection = new Set(selectedLabRequests);
    if (newSelection.has(labId)) {
      newSelection.delete(labId);
    } else {
      newSelection.add(labId);
    }
    setSelectedLabRequests(newSelection);

    // Update cost estimate - need to account for kits
    const selectedLabs = labRequestsToRender.filter(lab => newSelection.has(lab.id));
    const itemIds = [];
    const processedKits = new Set();

    selectedLabs.forEach(lab => {
      (lab.LabTestRequestDetails || []).forEach(detail => {
        if (detail.kit_id) {
          // This test is part of a kit - use kit's item_id instead
          if (!processedKits.has(detail.kit_id)) {
            processedKits.add(detail.kit_id);
            // Fetch kit item_id (we'll need to get it from the detail or make an API call)
            // For now, we'll collect kit_ids and fetch their item_ids
          }
        } else {
          // Regular test - use its item_id
          if (detail?.MedicalLabTest?.item_id) {
            itemIds.push(detail.MedicalLabTest.item_id);
          }
        }
      });
    });

    // Fetch kit item_ids for all kits
    if (processedKits.size > 0) {
      try {
        const kitIds = Array.from(processedKits);
        const kitResponse = await axios.post(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/lab/labTests/by-ids`,
          { ids: kitIds }
        );
        kitResponse.data.forEach(kit => {
          if (kit.item_id) {
            itemIds.push(kit.item_id);
          }
        });
      } catch (error) {
        console.error("Error fetching kit item_ids:", error);
      }
    }

    if (itemIds.length === 0) {
      setLabEstimate(0);
      return;
    }

    try {
      // Get unique item IDs - duplicates should be counted once
      const uniqueItemIds = [...new Set(itemIds)];
      
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/billing/estimate-cost`,
        { item_ids: uniqueItemIds }
      );
      
      setLabEstimate(response.data.estimated_cost);
    } catch (error) {
      console.error("Error fetching estimated cost:", error);
      setLabEstimate(0);
    }
  };

  const handleCheckboxChangeRadiology = async (radiologyId) => {
    const newSelection = new Set(selectedRadiologyRequests);
    if (newSelection.has(radiologyId)) {
      newSelection.delete(radiologyId);
    } else {
      newSelection.add(radiologyId);
    }
    setSelectedRadiologyRequests(newSelection);
    // Update cost estimate for radiology - need to account for kits
    const selectedRadiology = radiologyRequestsToRender.filter(lab => newSelection.has(lab.id));
    const itemIds = [];
    const processedKits = new Set();

    selectedRadiology.forEach(lab => {
      (lab.LabTestRequestDetails || []).forEach(detail => {
        if (detail.kit_id) {
          // This test is part of a kit - use kit's item_id instead
          if (!processedKits.has(detail.kit_id)) {
            processedKits.add(detail.kit_id);
          }
        } else {
          // Regular test - use its item_id
          if (detail?.MedicalLabTest?.item_id) {
            itemIds.push(detail.MedicalLabTest.item_id);
          }
        }
      });
    });

    // Fetch kit item_ids for all kits
    if (processedKits.size > 0) {
      try {
        const kitIds = Array.from(processedKits);
        const kitResponse = await axios.post(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/lab/labTests/by-ids`,
          { ids: kitIds }
        );
        kitResponse.data.forEach(kit => {
          if (kit.item_id) {
            itemIds.push(kit.item_id);
          }
        });
      } catch (error) {
        console.error("Error fetching kit item_ids:", error);
      }
    }

    if (itemIds.length === 0) {
      setRadiologyEstimate(0);
      return;
    }

    try {
      // Get unique item IDs - duplicates should be counted once
      const uniqueItemIds = [...new Set(itemIds)];
      
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/billing/estimate-cost`,
        { item_ids: uniqueItemIds }
      );
      
      setRadiologyEstimate(response.data.estimated_cost);
    } catch (error) {
      console.error("Error fetching estimated cost:", error);
      setRadiologyEstimate(0);
    }
  };

  const handleCheckboxChangeVisit = async (visitId) => {
    const newSelection = new Set(selectedVisits);
    if (newSelection.has(visitId)) {
      newSelection.delete(visitId);
      setSelectedVisits(newSelection);
      updateVisitCostEstimate(newSelection);
    } else {
      newSelection.add(visitId);
      setSelectedVisits(newSelection);
      updateVisitCostEstimate(newSelection);
    }
  };

  const handleCheckboxChangeProcedure = (procedureId) => {
    const procedures = billData?.procedures || [];
    const newSelection = new Set(selectedProcedures);
    if (newSelection.has(procedureId)) {
      newSelection.delete(procedureId);
    } else {
      newSelection.add(procedureId);
    }
    setSelectedProcedures(newSelection);

    const total = procedures
      .filter((procedure) => newSelection.has(procedure.procedure_id))
      .reduce((sum, procedure) => sum + getProcedureAgreedTotal(procedure.billing_details), 0);
    setProcedureEstimate(total);
  };

  const fetchDoctorInfo = async (visitId, doctorName) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/billing/get-doctor-item`,
        { doctor_name: doctorName }
      );
      
      if (response.data.item_id) {
        await new Promise(resolve => {
          setDoctorLineItems(prev => {
            const next = new Map(prev);
            next.set(visitId, {
              doctor_name: doctorName,
              item_id: response.data.item_id,
              quantity: 1
            });
            return next;
          });
          // allow React state to commit before estimate
          setTimeout(resolve, 0);
        });
      }
    } catch (error) {
      console.error("Error fetching doctor info:", error);
    }
  };

  // Prefill doctor line items so users see them before selecting a visit.
  // Uses a simple cache by doctor name to avoid repeated lookups.
  useEffect(() => {
    if (!billData?.visits || billData.visits.length === 0) return;

    let cancelled = false;

    const prefillDoctors = async () => {
      const doctorCache = new Map(); // doctor_name -> item_id|null
      const nextDoctorLineItems = new Map();

      for (const visit of billData.visits) {
        if (!visit || visit.patient_billing_id !== null) continue;
        if (!visit.assigned_doctor || visit.assigned_doctor === 'Not Assigned') continue;

        // Already have a doctor line item for this visit
        if (nextDoctorLineItems.has(visit.visit_id)) continue;

        const doctorName = visit.assigned_doctor;

        let itemId;
        if (doctorCache.has(doctorName)) {
          itemId = doctorCache.get(doctorName);
        } else {
          try {
            const response = await axios.post(
              `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/billing/get-doctor-item`,
              { doctor_name: doctorName }
            );
            itemId = response.data?.item_id ?? null;
          } catch (e) {
            itemId = null;
          }
          doctorCache.set(doctorName, itemId);
        }

        // Even if itemId is null, we still create the row (visible), but it won't be billed.
        nextDoctorLineItems.set(visit.visit_id, {
          doctor_name: doctorName,
          item_id: itemId,
          quantity: 1
        });
      }

      if (!cancelled) {
        setDoctorLineItems(nextDoctorLineItems);
      }
    };

    prefillDoctors();

    return () => {
      cancelled = true;
    };

  }, [billData]);

  const resetForm = () => {
    setSelectedLabRequests(new Set());
    setSelectedRadiologyRequests(new Set());
    setSelectedVisits(new Set());
    setSelectedProcedures(new Set());
    setCustomerId(null);
    setCareoffName("");
    setVisitEstimate(0);
    setLabEstimate(0);
    setRadiologyEstimate(0);
    setProcedureEstimate(0);
    setFormData({});
    setEditingVisits(new Set());
    setModifiedQuantities({});
    setDoctorLineItems(new Map());
    setPayThroughAccount(null);
    setSplitPayments(null);
    setShowSplitPaymentModal(false);
    setShowPayThroughModal(false);
    setShowSurgeryMergePrompt(false);
    setPendingAdmission(null);
    skipSurgeryMergePromptRef.current = false;
    surgeryMergeAdmissionRef.current = null;
    setEditingVisits(new Set());
    setModifiedQuantities({});
    setDoctorLineItems(new Map());
    setPayThroughAccount(null);
    setSplitPayments(null);
    setShowSplitPaymentModal(false);
    setShowPayThroughModal(false);
  };

  // Helper functions for quantity editing
  const handleToggleEditVisit = (visitId) => {
    const newEditing = new Set(editingVisits);
    if (newEditing.has(visitId)) {
      // User is accepting the changes (clicking checkmark)
      newEditing.delete(visitId);
      // Recalculate cost estimate if this visit is selected (includes doctor quantity changes)
      if (selectedVisits.has(visitId)) {
        updateVisitCostEstimate(selectedVisits);
      }
    } else {
      // User is entering edit mode (clicking pencil)
      newEditing.add(visitId);
    }
    setEditingVisits(newEditing);
  };

  const handleDoctorQuantityChange = (visitId, newQuantity) => {
    const qty = parseInt(newQuantity) || 0;
    setDoctorLineItems(prev => {
      const newMap = new Map(prev);
      const doctorInfo = newMap.get(visitId);
      if (doctorInfo) {
        newMap.set(visitId, { ...doctorInfo, quantity: qty });
      }
      return newMap;
    });
  };

  const handleQuantityChange = (visitId, itemIndex, newQuantity) => {
    const key = `${visitId}-${itemIndex}`;
    const qty = parseInt(newQuantity) || 0; // Allow zero quantities
    setModifiedQuantities(prev => ({
      ...prev,
      [key]: qty
    }));
    // Cost calculation will only happen when user clicks accept (checkmark)
  };

  const updateVisitCostEstimate = async (visitSelection = null) => {
    // Use passed selection or current state
    const currentSelection = visitSelection || selectedVisits;
    const selectedBillVisits = visitsToRender.filter(visit => currentSelection.has(visit.visit_id));
    
    if (selectedBillVisits.length === 0) {
      setVisitEstimate(0);
      return;
    }

    try {
      // Collect all items with their quantities (including doctors)
      const itemsWithQuantities = [];
      
      // Add doctor line items first
      for (const visitId of currentSelection) {
        const doctorInfo = doctorLineItems.get(visitId);
        if (doctorInfo && doctorInfo.item_id && doctorInfo.quantity > 0) {
          itemsWithQuantities.push({
            item_id: doctorInfo.item_id,
            quantity: doctorInfo.quantity,
            name: `Dr. ${doctorInfo.doctor_name} Consultation`
          });
        }
      }
      
      for (const visit of selectedBillVisits) {
        if (visit.prescription_items) {
          for (let itemIndex = 0; itemIndex < visit.prescription_items.length; itemIndex++) {
            const item = visit.prescription_items[itemIndex];
            if (item.item_id) {
              const quantity = getItemQuantity(visit.visit_id, itemIndex, item.quantity);
              // Only include items with quantity > 0
              if (quantity > 0) {
                itemsWithQuantities.push({
                  item_id: item.item_id,
                  quantity: quantity,
                  name: item.name
                });
              }
            }
          }
        }
      }

      if (itemsWithQuantities.length === 0) {
        setVisitEstimate(0);
        return;
      }

      // Calculate total cost accounting for quantities
      let totalEstimatedCost = 0;
      
      // Group items by item_id and sum quantities
      const itemQuantityMap = new Map();
      for (const item of itemsWithQuantities) {
        const currentQty = itemQuantityMap.get(item.item_id) || 0;
        itemQuantityMap.set(item.item_id, currentQty + item.quantity);
      }

      // Get prices for unique items
      const uniqueItemIds = Array.from(itemQuantityMap.keys());

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/billing/estimate-cost`,
        { item_ids: uniqueItemIds }
      );
      
      // The API returns estimated_cost which is the sum of all item prices (quantity 1 each)
      // We need to get individual prices, so let's call the API for each item individually
      for (const [itemId, totalQuantity] of itemQuantityMap.entries()) {
        try {
          const itemResponse = await axios.post(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/billing/estimate-cost`,
            { item_ids: [itemId] }
          );
          
          const itemPrice = itemResponse.data.estimated_cost;
          const itemTotal = itemPrice * totalQuantity;
          totalEstimatedCost += itemTotal;
        } catch (error) {
          console.error(`Error getting price for item ${itemId}:`, error);
        }
      }

      setVisitEstimate(totalEstimatedCost);
    } catch (error) {
      console.error("Error fetching estimated cost:", error);
      setVisitEstimate(0);
    }
  };

  const getItemQuantity = (visitId, itemIndex, originalQuantity) => {
    const key = `${visitId}-${itemIndex}`;
    return modifiedQuantities[key] !== undefined ? modifiedQuantities[key] : (originalQuantity || 1);
  };

  const handleProcessInvoice = async () => {
    if (cannotUpdateOldBill) {
      alert(ageEditReason || AGE_RESTRICTED_FALLBACK);
      return;
    }

    if (
      (!patient.company || patient.company === "") && patient.type === "Company") {
        alert("A company is required, please update the patient details");
        return;
    }
    
    if (selectedVisits.size === 0 && selectedLabRequests.size === 0 && selectedRadiologyRequests.size === 0 && selectedProcedures.size === 0) {
      alert("Please select at least one visit, lab request, radiology request, or surgery to process");
      return;
    }

    if (!branch) {
      alert("Invalid or empty branch!");
      return;
    }

    if (selectedProcedures.size > 0 && !bill?.zoho_sales_id && !skipSurgeryMergePromptRef.current) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/procedures/billing-context`,
          { params: { procedure_ids: Array.from(selectedProcedures).join(',') } }
        );
        if (response.data.admission) {
          setPendingAdmission(response.data.admission);
          setShowSurgeryMergePrompt(true);
          return;
        }
      } catch (error) {
        alert(error.response?.data?.error || error.message || "Could not check admission invoice.");
        return;
      }
    }

    const mergingAdmission = surgeryMergeAdmissionRef.current;
    if (
      patient.patient_type === "Private" &&
      !customerId &&
      !payThroughAccount &&
      !splitPayments &&
      !bill?.zoho_sales_id &&
      !mergingAdmission
    ) {
      setShowPayThroughModal(true);
      return;
    }

    await processInvoiceWithAccount(payThroughAccount, splitPayments);
  };

  const processInvoiceWithAccount = async (accountNumber = null, payments = null) => {
    setProcessing(true);
    try {
      const payload = {
        patient_id: patient.patient_id,
        zoho_sales_id: surgeryMergeAdmissionRef.current?.zoho_sales_id || bill?.zoho_sales_id || null,
        visit_ids: Array.from(selectedVisits),
        lab_request_ids: Array.from(selectedLabRequests),
        radiology_request_ids: Array.from(selectedRadiologyRequests),
        procedure_ids: Array.from(selectedProcedures),
        admission_id: surgeryMergeAdmissionRef.current?.admission_id || null,
        bill_id: bill?.id || null,
        branch,
        updatedBy: user.portal_id,
        customer_id: customerId || null,
        careoff_name: customerId === "4691741000000115145" ? careoffName : null,
        modified_quantities: modifiedQuantities,
        doctor_line_items: Object.fromEntries(
          Array.from(doctorLineItems.entries()).filter(([visitId, doctorInfo]) => {
            // Only send doctor items for selected visits; backend also ignores qty <= 0.
            return selectedVisits.has(visitId) && doctorInfo?.quantity > 0;
          })
        ),
        pay_through_account: payments ? null : (accountNumber || null),
        split_payment: !!payments,
        payments: payments || null,
      };

      if (
        surgeryMergeAdmissionRef.current &&
        !surgeryMergeAdmissionRef.current.zoho_sales_id &&
        patient.patient_type !== 'Company' &&
        !payload.customer_id
      ) {
        payload.customer_id = '4691741000000115035';
      }

      console.log("Processing invoice with payload:", payload); // Debug log

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/billing/process-patient-bill`,
        payload
      );
      console.log("Invoice processed successfully:", response.data);
      //setSalesViewRefresh(prev => prev + 1); // Trigger refresh of SalesView
      alert("Invoice processed successfully: " + response.data.sales_number);
      resetForm(); // Reset form after successful processing
      setFormData(response.data);
      // Refresh billing data
      const refreshResponse = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/billing/visit-bill/${patient.patient_id}`
      );
      setBillData(refreshResponse.data);
    } catch (error) {
      console.error("Error processing invoice:", error);
      alert(`Failed to process invoice. ${error.response?.data?.error || error.response?.data?.zoho_response || error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handlePayThroughAccountConfirm = (accountNumber) => {
    setPayThroughAccount(accountNumber);
    setSplitPayments(null);
    setShowPayThroughModal(false);
    processInvoiceWithAccount(accountNumber);
  };

  const handleSwitchToSplitPayment = () => {
    if (estimatedBillTotal <= 0) {
      alert("Please select bill items with a valid estimated price before split payment.");
      return;
    }
    setPayThroughAccount(null);
    setSplitPayments(null);
    setShowPayThroughModal(false);
    setShowSplitPaymentModal(true);
  };

  const handleSplitPaymentConfirm = (payments) => {
    setSplitPayments(payments);
    setPayThroughAccount(null);
    setShowSplitPaymentModal(false);
    processInvoiceWithAccount(null, payments);
  };

  const handleSplitPaymentClose = () => {
    setShowSplitPaymentModal(false);
    setShowPayThroughModal(true);
  };

  useEffect(() => {
    if (formData?.id) {
      const fetchBilledItems = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/billing/billed-requests/${formData.id}`
          );
          setBilledItems(response.data);
        } catch (error) {
          console.error("Error fetching billed items:", error);
        }
      };
      fetchBilledItems();
    }
  }, [formData.id]);

  // Fetch kit names for lab and radiology requests
  useEffect(() => {
    const fetchKitNames = async () => {
      const allKitIds = new Set();
      
      // Collect kit IDs from lab requests
      const labRequests = fetchedLabRequest || billData?.["lab-requests"];
      labRequests?.forEach(request => {
        request.LabTestRequestDetails?.forEach(detail => {
          if (detail.kit_id) {
            allKitIds.add(detail.kit_id);
          }
        });
      });
      
      // Collect kit IDs from radiology requests
      const radiologyRequests = fetchedRadiologyRequest || billData?.radiologyRequests;
      radiologyRequests?.forEach(request => {
        request.LabTestRequestDetails?.forEach(detail => {
          if (detail.kit_id) {
            allKitIds.add(detail.kit_id);
          }
        });
      });

      if (allKitIds.size > 0) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/lab/labTests/by-ids`,
            { ids: Array.from(allKitIds) }
          );
          const names = {};
          response.data.forEach(kit => {
            names[kit.id] = kit.test_name;
          });
          setKitNames(names);
        } catch (error) {
          console.error("Error fetching kit names:", error);
        }
      }
    };

    fetchKitNames();
  }, [fetchedLabRequest, fetchedRadiologyRequest, billData]);

  if (loading) {
    return <div>Loading billing data...</div>;
  }

  // Decide which data to render for each section.
  const labRequestsToRender = fetchedLabRequest
    ? fetchedLabRequest
    : billData?.["lab-requests"];
  const radiologyRequestsToRender = fetchedRadiologyRequest
    ? fetchedRadiologyRequest
    : billData?.radiologyRequests;
  const visitsToRender = fetchedVisit ? fetchedVisit : billData?.visits;
  const proceduresToRender = billData?.procedures;

  const renderBillingSection = (requestsToRender, type) => {
    const selectedItems = type === "Lab" ? selectedLabRequests : selectedRadiologyRequests;
    const handleChange = type === "Lab" ? handleCheckboxChangeLab : handleCheckboxChangeRadiology;

    // Helper function to group tests by kit
    const groupTestsByKit = (details) => {
      const kits = new Map();
      const standaloneTests = [];

      details.forEach(detail => {
        if (detail.kit_id) {
          if (!kits.has(detail.kit_id)) {
            kits.set(detail.kit_id, {
              kitId: detail.kit_id,
              tests: []
            });
          }
          kits.get(detail.kit_id).tests.push(detail);
        } else {
          standaloneTests.push(detail);
        }
      });

      return { kits, standaloneTests };
    };

    return (
      <div className="bill-section">
        <h3>{type === "Lab" ? "Lab Requests" : "Radiology Requests"}</h3>
        <div className="scrollable-section">
          {requestsToRender && requestsToRender.length > 0 ? (
            <ul>
              {requestsToRender.map((request) => {
                const { kits, standaloneTests } = request.LabTestRequestDetails 
                  ? groupTestsByKit(request.LabTestRequestDetails)
                  : { kits: new Map(), standaloneTests: [] };

                return (
                  <li key={request.id}>
                    <label>
                      <input
                        type="checkbox"
                        name={type === "Lab" ? "labRequest" : "radiologyRequest"}
                        onChange={() => handleChange(request.id)}
                        checked={selectedItems.has(request.id)}
                        disabled={request.patient_billing_id !== null}
                      />
                      {type} Request #{request.id} - {request.status} - Requested By {request.requested_by}
                      {request.patient_billing_id && <span className="billed-indicator"> (Already Billed)</span>}
                    </label>
                    {(kits.size > 0 || standaloneTests.length > 0) && (
                      <ul className="child-details">
                        {/* Render kits first */}
                        {Array.from(kits.entries()).map(([kitId, kitData]) => {
                          const kitName = kitNames[kitId] || `Kit #${kitId}`;
                          return (
                            <li key={`kit-${kitId}`} style={{ marginBottom: '10px' }}>
                              <div style={{ 
                                fontWeight: 'bold', 
                                color: '#2c5282',
                                marginBottom: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px'
                              }}>
                                <span>📦</span>
                                <span>{kitName}</span>
                              </div>
                              <ul style={{ 
                                marginLeft: '24px', 
                                marginTop: '4px',
                                borderLeft: '2px solid #e2e8f0',
                                paddingLeft: '12px'
                              }}>
                                {kitData.tests.map((detail) => (
                                  <li
                                    key={detail.id}
                                    style={{ 
                                      fontSize: '0.92em', 
                                      color: '#4a5568',
                                      marginBottom: '2px',
                                      listStyleType: 'circle'
                                    }}
                                  >
                                    {detail.MedicalLabTest
                                      ? labTestDisplayName(detail.MedicalLabTest.test_name, detail.side)
                                      : "Unknown Test"}
                                  </li>
                                ))}
                              </ul>
                            </li>
                          );
                        })}
                        {/* Render standalone tests */}
                        {standaloneTests.map((detail) => (
                          <li
                            key={detail.id}
                            className={
                              !detail.MedicalLabTest.item_id ? "highlight" : ""
                            }
                          >
                            {detail.MedicalLabTest
                              ? labTestDisplayName(detail.MedicalLabTest.test_name, detail.side)
                              : "Unknown Test"}
                            {detail.MedicalLabTest.item_id ? (
                              ""
                            ) : (
                              <span>
                                {" "}
                                * This item should be added manually to the invoice or sales receipt
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p>There are no {type.toLowerCase()} requests to bill for this patient.</p>
          )}
        </div>
      </div>
    );
  };

  // Add new render function for billed items
  const renderBilledItems = () => {
    if (!billedItems) return null;
    
    return (
      <div className="billed-items-section">
        <h3>Processed Items</h3>
        {billedItems.labRequests?.length > 0 && (
          <div className="bill-section">
            <h4>Lab Requests</h4>
            <ul>
              {billedItems.labRequests.map(request => (
                <li key={request.id}>
                  <span>Lab Request #{request.id}</span>
                  {request.LabTestRequestDetails && (
                    <ul className="child-details">
                      {request.LabTestRequestDetails.map(detail => (
                        <li key={detail.id}>
                          {detail.MedicalLabTest
                            ? labTestDisplayName(detail.MedicalLabTest.test_name, detail.side)
                            : "Unknown Test"}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {billedItems.visits?.length > 0 && (
          <div className="bill-section">
            <h4>Visits</h4>
            <ul>
              {billedItems.visits.map(visit => (
                <li key={visit.visit_id}>
                  <span>Visit #{visit.visit_id} on {new Date(visit.visit_date).toLocaleDateString()}</span>
                  {visit.prescription_items && visit.prescription_items.length > 0 && (
                    <ul className="child-details">
                      {visit.prescription_items.map((item, idx) => (
                        <li key={idx} className={!item.item_id ? "highlight" : ""}>
                          {item.name || "Prescription Item"} (Qty {item.quantity}-{item.instructions}-{item.duration})
                          {!item.item_id && (
                            <span> * This item should be added manually to the invoice or sales receipt</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {billedItems.procedures?.length > 0 && (
          <div className="bill-section">
            <h4>Surgeries</h4>
            <ul>
              {billedItems.procedures.map((procedure) => (
                <li key={procedure.procedure_id}>
                  <span>
                    {procedure.procedure_name} — {formatAgreedAmount(getProcedureAgreedTotal(procedure.billing_details))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="patient-bill-form-container">
      {showSurgeryMergePrompt && pendingAdmission && (
        <AdmissionInvoicePrompt
          admission={pendingAdmission}
          onMerge={() => {
            surgeryMergeAdmissionRef.current = pendingAdmission;
            skipSurgeryMergePromptRef.current = true;
            setShowSurgeryMergePrompt(false);
            handleProcessInvoice();
          }}
          onNew={() => {
            surgeryMergeAdmissionRef.current = null;
            skipSurgeryMergePromptRef.current = true;
            setShowSurgeryMergePrompt(false);
            handleProcessInvoice();
          }}
          onClose={() => setShowSurgeryMergePrompt(false)}
        />
      )}
      {showPayThroughModal && (
        <PayThroughAccountModal
          onClose={() => setShowPayThroughModal(false)}
          onConfirm={handlePayThroughAccountConfirm}
          onSplitPayment={handleSwitchToSplitPayment}
          allowSplitPayment={allowSplitPayment}
          defaultAccount={payThroughAccount}
          branch={branch}
          registerClosed={registerClosed}
        />
      )}
      {showSplitPaymentModal && (
        <SplitPaymentModal
          onClose={handleSplitPaymentClose}
          onConfirm={handleSplitPaymentConfirm}
          billTotal={estimatedBillTotal}
          branch={branch}
          registerClosed={registerClosed}
        />
      )}
      <div className="modal-header">
        <div className="header-content">
          <div className="primary-header">
            <h2>{bill ? "Update Sales Record" : "Process Patient Bill"}</h2>
            <div className="patient-info">
              <p>
                <strong>Patient:</strong>{" "}
                {bill ? bill.Patient.patient_number : patient.patient_number}
              </p>
              <p className="estimated-fees">
                <strong>Est. Price:</strong>{" "}
                {Number(visitEstimate) + Number(labEstimate) + Number(radiologyEstimate) + Number(procedureEstimate)}
              </p>
              {bill?.sales_number && (
                <p>
                  <strong>Sales#:</strong> {bill.sales_number}
                </p>
              )}
              {bill?.date && (
                <p>
                  <strong>Bill Date:</strong> {formatBillDate(bill.date)}
                </p>
              )}
            </div>
          </div>
          <div className="secondary-header">
            {patient.patient_type === "Private" && !bill?.sales_number && (
              <div className="form-group">
                <select
                  id="customer-select"
                  value={customerId || ""}
                  onChange={(e) => {
                    setCustomerId(e.target.value);
                    if (e.target.value) {
                      setSplitPayments(null);
                      setPayThroughAccount(null);
                    }
                  }}
                >
                  <option value="">Private Bill Type</option>
                  {customerOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
                {customerId === "4691741000000115145" && (
                  <div>
                    <label htmlFor="careoff-name" className="requiredLabel">
                      <strong>Careoff Name*</strong>
                    </label>
                    <input
                      type="text"
                      id="careoff-name"
                      value={careoffName}
                      onChange={(e) => setCareoffName(e.target.value)}
                      placeholder="Enter Careoff Name"
                    />
                  </div>
                )}
              </div>
            )}
            <div className="branch-selector">
              <select
                id="branch-select"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
              >
                <option value="">Branch*</option>
                <option value="Pultney">Pultney</option>
                <option value="Kissy">Kissy</option>
              </select>
            </div>
          </div>
        </div>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>

      <div className="split-screen">
        <div className="left-panel">
          <div className="billing-sections">
            {renderBilledItems()}
            <div className="unbilled-items-section">
              <h3>Unbilled Items</h3>
              <div className="patient-bill-form modal-content">
                {visitsToRender || labRequestsToRender || proceduresToRender ? (
                  <>
                    {renderBillingSection(labRequestsToRender, "Lab")}
                    {renderBillingSection(radiologyRequestsToRender, "Radiology")}

                    <div className="bill-section">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <h3>Visit Prescriptions and Consultations</h3>
                      </div>
                      <div className="scrollable-section">
                        {visitsToRender && visitsToRender.length > 0 ? (
                          <ul>
                            {visitsToRender.map(
                              (visit) =>
                                (visit.prescription_items &&
                                  visit.prescription_items.length > 0) ||
                                doctorLineItems.has(visit.visit_id) ? (
                                  <li key={visit.visit_id}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                      <label>
                                        <input
                                          type="checkbox"
                                          name="visit"
                                          onChange={() =>
                                            handleCheckboxChangeVisit(visit.visit_id)
                                          }
                                          checked={selectedVisits.has(visit.visit_id)}
                                          disabled={visit.patient_billing_id !== null}
                                        />
                                        Visit #{visit.visit_id} on{" "}
                                        {new Date(visit.visit_date).toLocaleDateString()}
                                        {visit.patient_billing_id && <span className="billed-indicator"> (Already Billed)</span>}
                                      </label>
                                      {/* Only show edit button for unprocessed visits */}
                                      {visit.patient_billing_id === null && (
                                        <button
                                          type="button"
                                          onClick={() => handleToggleEditVisit(visit.visit_id)}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            color: '#666',
                                            padding: '2px'
                                          }}
                                          title={editingVisits.has(visit.visit_id) ? "Accept quantity changes" : "Edit quantities"}
                                        >
                                          {editingVisits.has(visit.visit_id) ? '✓' : '✏️'}
                                        </button>
                                      )}
                                    </div>
                                    {(visit.prescription_items || doctorLineItems.has(visit.visit_id)) && (
                                      <ul className="child-details">
                                        {/* Show doctor consultation as first item if available */}
                                        {doctorLineItems.has(visit.visit_id) && (
                                          <li key="doctor" className="doctor-item">
                                            <span>Dr. {doctorLineItems.get(visit.visit_id).doctor_name} - Consultation</span>
                                            {editingVisits.has(visit.visit_id) && selectedVisits.has(visit.visit_id) ? (
                                              <span>
                                                {" (Qty "}
                                                <input
                                                  type="number"
                                                  min="0"
                                                  value={doctorLineItems.get(visit.visit_id).quantity}
                                                  onChange={(e) => handleDoctorQuantityChange(visit.visit_id, e.target.value)}
                                                  style={{
                                                    width: '50px',
                                                    padding: '2px 4px',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '3px'
                                                  }}
                                                />
                                                )
                                              </span>
                                            ) : (
                                              <span>
                                                {" (Qty "}
                                                {doctorLineItems.get(visit.visit_id).quantity}
                                                )
                                              </span>
                                            )}
                                            {!doctorLineItems.get(visit.visit_id).item_id && (
                                              <span>
                                                {" "}
                                                * Doctor item missing in inventory
                                              </span>
                                            )}
                                          </li>
                                        )}
                                        {/* Show prescription items */}
                                        {(visit.prescription_items || []).map((item, idx) => (
                                          <li
                                            key={idx}
                                            className={!item.item_id ? "highlight" : ""}
                                          >
                                            <span>{item.name || "Prescription Item"}</span>
                                            {editingVisits.has(visit.visit_id) && item.item_id ? (
                                              <span>
                                                {" (Qty "}
                                                <input
                                                  type="number"
                                                  min="0"
                                                  value={getItemQuantity(visit.visit_id, idx, item.quantity)}
                                                  onChange={(e) => handleQuantityChange(visit.visit_id, idx, e.target.value)}
                                                  style={{
                                                    width: '50px',
                                                    padding: '2px 4px',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '3px'
                                                  }}
                                                />
                                                {item.instructions && `-${item.instructions}`})
                                              </span>
                                            ) : (
                                              <span>
                                                {" (Qty "}
                                                {getItemQuantity(visit.visit_id, idx, item.quantity)}
                                                {item.instructions && `-${item.instructions}`})
                                              </span>
                                            )}
                                            {!item.item_id && (
                                              <span>
                                                {" "}
                                                * This item should be added manually to the invoice or sales receipt
                                              </span>
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </li>
                                ) : null
                            )}
                          </ul>
                        ) : (
                          <p>
                            There are no prescriptions or consultations to bill for this
                            patient.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="bill-section">
                      <h3>Surgeries</h3>
                      <div className="scrollable-section">
                        {proceduresToRender && proceduresToRender.length > 0 ? (
                          <ul>
                            {proceduresToRender.map((procedure) => {
                              const agreedTotal = getProcedureAgreedTotal(procedure.billing_details);
                              const hasPrices = agreedTotal > 0;
                              return (
                                <li key={procedure.procedure_id}>
                                  <label>
                                    <input
                                      type="checkbox"
                                      name="procedure"
                                      onChange={() => handleCheckboxChangeProcedure(procedure.procedure_id)}
                                      checked={selectedProcedures.has(procedure.procedure_id)}
                                      disabled={!hasPrices}
                                    />
                                    {procedure.procedure_name}
                                    {procedure.scheduled_date
                                      ? ` — ${new Date(procedure.scheduled_date).toLocaleDateString()}`
                                      : ''}
                                    {` — ${formatAgreedAmount(agreedTotal)}`}
                                    {!hasPrices && (
                                      <span> (Enter prices on the Pricing tab before billing)</span>
                                    )}
                                  </label>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p>There are no unpaid surgeries to bill for this patient.</p>
                        )}
                      </div>
                    </div>
                    {salesNumber && (
                      <div className="invoice-result">
                        <p>
                          Invoice processed successfully. Sales Number:{" "}
                          <strong>{salesNumber}</strong>
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <p>No billing data available.</p>
                )}
              </div>
              {hasPermission("/patient-billing", "Add") && (
                <div className="invoice-actions">
                  <button
                    onClick={handleProcessInvoice}
                    disabled={processing || hasPendingVisitEdits || cannotUpdateOldBill}
                    title={
                      cannotUpdateOldBill
                        ? (ageEditReason || AGE_RESTRICTED_FALLBACK)
                        : hasPendingVisitEdits
                        ? "Accept visit edits before processing"
                        : undefined
                    }
                  >
                    {processing
                      ? "Processing..."
                      : bill
                      ? "Update Sales Record"
                      : "Process Invoice"}
                  </button>
                  {cannotUpdateOldBill && (
                    <div className="invoice-age-hint">
                      {ageEditReason || AGE_RESTRICTED_FALLBACK}
                    </div>
                  )}
                  {hasPendingVisitEdits && !cannotUpdateOldBill && (
                    <div
                      style={{
                        marginTop: '6px',
                        fontSize: '12px',
                        color: '#666'
                      }}
                    >
                      Accept visit edits by clicking ✓ to continue.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="right-panel">
          {formData?.sales_number && <SalesView salesNumber={formData.sales_number} />}
        </div>
      </div>
    </div>
  );
};

export default PatientBillingForm;
